import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { NavLink, Navigate, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CalendarClock, Clock, LogOut, UserCog, KeyRound, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const TILES = [
  { to: "/app/mijn-rooster", label: "Mijn rooster", desc: "Bekijk je eigen rooster van deze week", icon: CalendarClock },
  { to: "/app/inklok",       label: "Inkloksysteem", desc: "Zelf inklokken, uitklokken en pauze registreren", icon: Clock },
];

function PinCard() {
  const [hasPin, setHasPin] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/staff-pin/has").then(res => setHasPin(res.data?.has_pin)).catch(() => setHasPin(false));
  }, []);

  const save = async () => {
    if (!/^\d{6}$/.test(pin)) return toast.error("PIN moet exact 6 cijfers zijn");
    if (pin !== confirmPin) return toast.error("PIN's komen niet overeen");
    setSaving(true);
    try {
      await api.post("/staff-pin/set", { pin });
      toast.success("PIN opgeslagen");
      setHasPin(true);
      setShowForm(false);
      setPin(""); setConfirmPin("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kon PIN niet opslaan");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-6" data-testid="pin-card">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-lg bg-[color:var(--sage-100)] flex items-center justify-center">
          <KeyRound className="h-5 w-5 text-[color:var(--sage)]" strokeWidth={2} />
        </div>
        <div>
          <div className="font-display text-base font-semibold">Persoonlijke PIN</div>
          <div className="text-xs text-neutral-500">
            Voor identificatie bij medicatie/incident via de groepslogin
          </div>
        </div>
        {hasPin === true && (
          <span data-testid="pin-status-set" className="ml-auto inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full">
            <Check className="h-3 w-3" /> Ingesteld
          </span>
        )}
        {hasPin === false && (
          <span data-testid="pin-status-missing" className="ml-auto inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded-full">
            <X className="h-3 w-3" /> Nog niet ingesteld
          </span>
        )}
      </div>

      {!showForm && (
        <Button size="sm" variant="outline" onClick={() => setShowForm(true)} data-testid="pin-set-btn" className="mt-3">
          {hasPin ? "PIN wijzigen" : "PIN instellen"}
        </Button>
      )}

      {showForm && (
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs text-neutral-600">Nieuwe PIN (6 cijfers)</label>
            <Input type="password" value={pin} maxLength={6} inputMode="numeric"
              onChange={e => setPin(e.target.value.replace(/[^0-9]/g, ""))}
              data-testid="pin-input" placeholder="••••••" className="tracking-widest" />
          </div>
          <div>
            <label className="text-xs text-neutral-600">Bevestig PIN</label>
            <Input type="password" value={confirmPin} maxLength={6} inputMode="numeric"
              onChange={e => setConfirmPin(e.target.value.replace(/[^0-9]/g, ""))}
              data-testid="pin-confirm-input" placeholder="••••••" className="tracking-widest" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={saving} data-testid="pin-save-btn">
              {saving ? "Bezig…" : "Opslaan"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>Annuleren</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PersonalStaffDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (user && user.role !== "pedagogisch_medewerker") {
    return <Navigate to="/app" replace />;
  }
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-[color:var(--bone)]">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10" data-testid="staff-topbar">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[color:var(--sage)] flex items-center justify-center">
              <UserCog className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-lg leading-tight" data-testid="staff-account-name">{user.name}</div>
              <div className="text-xs text-neutral-500">Persoonlijk medewerker-account</div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => { await logout(); navigate("/login"); }}
            data-testid="staff-logout"
          >
            <LogOut className="h-4 w-4 mr-2" /> Uitloggen
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="staff-dashboard">
        <div className="mb-8">
          <h1 className="font-display text-3xl tracking-tight text-neutral-900">Welkom, {user.name}</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Persoonlijk dashboard — alleen jouw rooster en jouw inkloksysteem. Voor groepstaken:
            log in met de groepslogin van jouw opvanggroep.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-testid="staff-tiles">
          {TILES.map(t => (
            <NavLink key={t.label} to={t.to}
              data-testid={`staff-tile-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="block bg-white border border-neutral-200 rounded-xl p-6 hover:border-[color:var(--sage)] transition-colors group">
              <div className="w-12 h-12 rounded-lg bg-[color:var(--sage-100)] flex items-center justify-center mb-4">
                <t.icon className="h-6 w-6 text-[color:var(--sage)]" strokeWidth={2} />
              </div>
              <div className="font-display text-lg font-semibold text-neutral-900 group-hover:text-[color:var(--sage)] transition-colors">
                {t.label}
              </div>
              <p className="text-sm text-neutral-500 mt-2">{t.desc}</p>
            </NavLink>
          ))}
        </div>

        <PinCard />
      </main>
    </div>
  );
}
